import type { ProviderId } from '../types';
import type { Provider } from './provider';

const cache = new Map<string, Promise<Provider>>();

/**
 * Providers hold an SDK client, so they are cached per provider and key. The SDKs are imported lazily:
 * the app shell loads without them and only the chosen provider's code is ever downloaded.
 */
export function createProvider(id: ProviderId, apiKey: string, selectedModel: string): Promise<Provider> {
  const cacheKey = `${id}:${apiKey}:${id === 'openrouter' ? selectedModel : ''}`;
  const cached = cache.get(cacheKey);
  if (cached) return cached;
  const created = instantiate(id, apiKey, selectedModel);
  cache.set(cacheKey, created);
  created.catch(() => cache.delete(cacheKey));
  return created;
}

async function instantiate(id: ProviderId, apiKey: string, selectedModel: string): Promise<Provider> {
  switch (id) {
    case 'anthropic': {
      const { AnthropicProvider } = await import('./anthropic');
      return new AnthropicProvider(apiKey);
    }
    case 'openai': {
      const { OpenAIProvider } = await import('./openai');
      return new OpenAIProvider(apiKey);
    }
    case 'openrouter': {
      const { OpenRouterProvider } = await import('./openrouter');
      return new OpenRouterProvider(apiKey, selectedModel);
    }
    case 'demo': {
      const { DemoProvider } = await import('./demo');
      return new DemoProvider();
    }
  }
}

export { PROVIDERS, providerMeta } from './catalog';
export { ProviderError, isAbort } from './provider';
export type { Provider, StreamEvent, StreamRequest, ModelOption, ToolDefinition } from './provider';
